/**
 * SubCatalogsPanel — экран C админки СЗР: подсправочники (AK-385).
 *
 * CRUD пяти pesticide-specific подсправочников (эталон reference-CRUD, стиль
 * Manufacturers): действующие вещества, регистранты, преп. формы, категории,
 * объекты обработки. Удаление = архивирование (archive-guard на бэке: 409 если
 * запись используется). Ед. измерения — только чтение (общий справочник),
 * показываем ссылкой.
 *
 * Inner-селектор (вкладки) переключает активный подсправочник. Inline create +
 * archive; поля зависят от подсправочника (вещество: name_ru+ISO; регистрант:
 * name+ОГРН; объект обработки: name+category; формы/категории: code+name).
 */
import { useState } from 'react';
import { Archive, Check, ExternalLink, Loader2, Lock, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useArchiveRegistrant,
  useArchiveSubItem,
  useArchiveSubstance,
  useArchiveTreatedObject,
  useCreateCodeName,
  useCreateRegistrant,
  useCreateSubstance,
  useCreateTreatedObject,
  useSubCatalog,
  useSubstancesList,
} from '@/lib/pesticides/queries';
import { toast } from '@/lib/toast';

type CatalogKey =
  | 'substances'
  | 'registrants'
  | 'formulations'
  | 'categories'
  | 'treated-objects';

const CATALOGS: { key: CatalogKey; label: string }[] = [
  { key: 'substances', label: 'Действующие вещества' },
  { key: 'registrants', label: 'Регистранты' },
  { key: 'formulations', label: 'Преп. формы' },
  { key: 'categories', label: 'Категории' },
  { key: 'treated-objects', label: 'Объекты обработки' },
];

// ─── substances (name_ru + ISO) ───

function SubstancesTable() {
  const q = useSubstancesList();
  const createMut = useCreateSubstance();
  const archiveMut = useArchiveSubstance();
  const items = q.data?.items ?? [];

  const [creating, setCreating] = useState(false);
  const [nameRu, setNameRu] = useState('');
  const [iso, setIso] = useState('');

  const reset = () => {
    setCreating(false);
    setNameRu('');
    setIso('');
  };
  const save = async () => {
    if (!nameRu.trim()) return;
    try {
      await createMut.mutateAsync({
        name_ru: nameRu.trim(),
        name_iso: iso.trim() || null,
      });
      toast.success('Вещество добавлено');
      reset();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const archive = async (id: string, name: string) => {
    if (!window.confirm(`Архивировать «${name}»?`)) return;
    try {
      await archiveMut.mutateAsync(id);
      toast.success('Архивировано');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <CrudCard
      title="Действующие вещества"
      hint="Специфично для пестицидов. Удаление = архивирование; заблокировано, если вещество используется препаратами."
      onCreate={() => setCreating(true)}
      creating={creating}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название (рус)</TableHead>
            <TableHead className="w-[200px]">ISO</TableHead>
            <TableHead className="w-[90px] text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={3}>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
              </TableRow>
            ))}
          {creating && (
            <TableRow>
              <TableCell>
                <Input
                  autoFocus
                  value={nameRu}
                  onChange={(e) => setNameRu(e.target.value)}
                  placeholder="Клотианидин"
                />
              </TableCell>
              <TableCell>
                <Input
                  value={iso}
                  onChange={(e) => setIso(e.target.value)}
                  placeholder="Clothianidin"
                />
              </TableCell>
              <TableCell className="text-right">
                <SaveCancel onSave={() => void save()} onCancel={reset} disabled={!nameRu.trim()} busy={createMut.isPending} />
              </TableCell>
            </TableRow>
          )}
          {items.map((s) => (
            <TableRow key={s.id}>
              <TableCell className="font-medium text-foreground">
                {s.name_ru}
              </TableCell>
              <TableCell className="text-sm italic text-muted-foreground">
                {s.name_iso ?? '—'}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => void archive(s.id, s.name_ru)}>
                  <Archive className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EmptyHint show={!q.isLoading && items.length === 0 && !creating} />
    </CrudCard>
  );
}

// ─── registrants (name + ОГРН) ───

function RegistrantsTable() {
  const q = useSubCatalog('registrants');
  const createMut = useCreateRegistrant();
  const archiveMut = useArchiveRegistrant();
  const items = q.data ?? [];

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [ogrn, setOgrn] = useState('');
  const reset = () => {
    setCreating(false);
    setName('');
    setOgrn('');
  };
  const save = async () => {
    if (!name.trim()) return;
    try {
      await createMut.mutateAsync({ name: name.trim(), ogrn: ogrn.trim() || null });
      toast.success('Регистрант добавлен');
      reset();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const archive = async (id: string, n: string) => {
    if (!window.confirm(`Архивировать «${n}»?`)) return;
    try {
      await archiveMut.mutateAsync(id);
      toast.success('Архивировано');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <CrudCard
      title="Регистранты"
      hint="Заявители препаратов. Удаление = архивирование; заблокировано, если на регистранта ссылаются препараты."
      onCreate={() => setCreating(true)}
      creating={creating}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead className="w-[200px]">ОГРН</TableHead>
            <TableHead className="w-[90px] text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={3}>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
              </TableRow>
            ))}
          {creating && (
            <TableRow>
              <TableCell>
                <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Байер КропСайенс АГ" />
              </TableCell>
              <TableCell>
                <Input value={ogrn} onChange={(e) => setOgrn(e.target.value)} placeholder="—" />
              </TableCell>
              <TableCell className="text-right">
                <SaveCancel onSave={() => void save()} onCancel={reset} disabled={!name.trim()} busy={createMut.isPending} />
              </TableCell>
            </TableRow>
          )}
          {items.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-medium text-foreground">{r.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.code ?? '—'}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => void archive(r.id, r.name)}>
                  <Archive className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EmptyHint show={!q.isLoading && items.length === 0 && !creating} />
    </CrudCard>
  );
}

// ─── code + name (formulations / categories) ───

function CodeNameTable({
  kind,
  title,
  hint,
  namePlaceholder,
}: {
  kind: 'formulations' | 'categories';
  title: string;
  hint: string;
  namePlaceholder: string;
}) {
  const q = useSubCatalog(kind);
  const createMut = useCreateCodeName(kind);
  const archiveMut = useArchiveSubItem(kind);
  const items = q.data ?? [];

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const reset = () => {
    setCreating(false);
    setCode('');
    setName('');
  };
  const save = async () => {
    if (!name.trim()) return;
    try {
      await createMut.mutateAsync({ code: code.trim() || null, name: name.trim() });
      toast.success('Добавлено');
      reset();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const archive = async (id: string, n: string) => {
    if (!window.confirm(`Архивировать «${n}»?`)) return;
    try {
      await archiveMut.mutateAsync(id);
      toast.success('Архивировано');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <CrudCard title={title} hint={hint} onCreate={() => setCreating(true)} creating={creating}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">Код</TableHead>
            <TableHead>Название</TableHead>
            <TableHead className="w-[90px] text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={3}>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
              </TableRow>
            ))}
          {creating && (
            <TableRow>
              <TableCell>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="код" />
              </TableCell>
              <TableCell>
                <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={namePlaceholder} />
              </TableCell>
              <TableCell className="text-right">
                <SaveCancel onSave={() => void save()} onCancel={reset} disabled={!name.trim()} busy={createMut.isPending} />
              </TableCell>
            </TableRow>
          )}
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>
                <code className="text-xs text-muted-foreground">{it.code ?? '—'}</code>
              </TableCell>
              <TableCell className="font-medium text-foreground">{it.name}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => void archive(it.id, it.name)}>
                  <Archive className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EmptyHint show={!q.isLoading && items.length === 0 && !creating} />
    </CrudCard>
  );
}

// ─── treated objects (name + category) ───

function TreatedObjectsTable() {
  const q = useSubCatalog('treated-objects');
  const createMut = useCreateTreatedObject();
  const archiveMut = useArchiveTreatedObject();
  const items = q.data ?? [];

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const reset = () => {
    setCreating(false);
    setName('');
    setCategory('');
  };
  const save = async () => {
    if (!name.trim() || !category.trim()) return;
    try {
      await createMut.mutateAsync({ name: name.trim(), category: category.trim() });
      toast.success('Объект добавлен');
      reset();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const archive = async (id: string, n: string) => {
    if (!window.confirm(`Архивировать «${n}»?`)) return;
    try {
      await archiveMut.mutateAsync(id);
      toast.success('Архивировано');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <CrudCard
      title="Объекты обработки"
      hint="Не-культурные цели регламентов (склады, теплицы, участки…). Категория обязательна. Удаление = архивирование; заблокировано, если используется в регламентах."
      onCreate={() => setCreating(true)}
      creating={creating}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead className="w-[180px]">Категория</TableHead>
            <TableHead className="w-[90px] text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={3}>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
              </TableRow>
            ))}
          {creating && (
            <TableRow>
              <TableCell>
                <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Складские помещения" />
              </TableCell>
              <TableCell>
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="storage" />
              </TableCell>
              <TableCell className="text-right">
                <SaveCancel onSave={() => void save()} onCancel={reset} disabled={!name.trim() || !category.trim()} busy={createMut.isPending} />
              </TableCell>
            </TableRow>
          )}
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell className="font-medium text-foreground">{it.name}</TableCell>
              <TableCell>
                {it.code ? (
                  <Badge variant="secondary">{it.code}</Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => void archive(it.id, it.name)}>
                  <Archive className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EmptyHint show={!q.isLoading && items.length === 0 && !creating} />
    </CrudCard>
  );
}

// ─── shared shells ───

function CrudCard({
  title,
  hint,
  onCreate,
  creating,
  children,
}: {
  title: string;
  hint: string;
  onCreate: () => void;
  creating: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        <Button size="sm" onClick={onCreate} disabled={creating}>
          <Plus className="h-4 w-4" />
          Добавить
        </Button>
      </div>
      <div className="overflow-x-auto">{children}</div>
      <p className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
        {hint}
      </p>
    </Card>
  );
}

function SaveCancel({
  onSave,
  onCancel,
  disabled,
  busy,
}: {
  onSave: () => void;
  onCancel: () => void;
  disabled: boolean;
  busy: boolean;
}) {
  return (
    <div className="flex justify-end gap-1">
      <Button size="sm" onClick={onSave} disabled={disabled || busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
      <Button size="sm" variant="outline" onClick={onCancel}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

function EmptyHint({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
      Пока нет записей — добавьте первую.
    </div>
  );
}

// ─── units (read-only reference) ───

function UnitsReadOnlyNote() {
  return (
    <Card className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
      <Lock className="h-4 w-4 shrink-0" />
      <span>
        Единицы измерения редактируются в общем справочнике системы, не здесь.
      </span>
      <a
        href="/platform-admin/reference/crops"
        className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"
      >
        Общий справочник <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </Card>
  );
}

export function SubCatalogsPanel() {
  const [active, setActive] = useState<CatalogKey>('substances');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1">
        {CATALOGS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setActive(c.key)}
            className={
              'rounded-md px-3 py-1.5 text-sm transition-colors ' +
              (active === c.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent')
            }
          >
            {c.label}
          </button>
        ))}
      </div>

      {active === 'substances' && <SubstancesTable />}
      {active === 'registrants' && <RegistrantsTable />}
      {active === 'formulations' && (
        <CodeNameTable
          kind="formulations"
          title="Препаративные формы"
          hint="Форма выпуска (КС, СК, ВДГ…). Удаление = архивирование; заблокировано, если форма используется препаратами."
          namePlaceholder="Концентрат суспензии"
        />
      )}
      {active === 'categories' && (
        <CodeNameTable
          kind="categories"
          title="Категории"
          hint="Категории препаратов (инсектицид, фунгицид…). Удаление = архивирование; заблокировано, если категория используется препаратами."
          namePlaceholder="Инсектицид"
        />
      )}
      {active === 'treated-objects' && <TreatedObjectsTable />}

      <UnitsReadOnlyNote />
    </div>
  );
}
